<template>
  <span ref="select-plus" class="slect-plus" :style="`width: ${localWidth}px`">
    <div class="select-area" :style="`width: ${localWidth};`">
      <a-tooltip placement="top" :title="tooltip">
        <a-select
          v-bind="$attrs"
          :key="key"
          show-search
          :disabled="disabled"
          style="width: 100%"
          :value="selectValue"
          :show-arrow="false"
          :mode="multiple ? 'multiple' : 'default'"
          :filter-option="false"
          :placeholder="placeholder ? placeholder : ''"
          :maxTagCount="1"
          :required="required"
          :allowClear="allowClear"
          optionLabelProp="label"
          :dropdownClassName="dropdownClassName"
          :dropdownMatchSelectWidth="dropdownMatchSelectWidth"
          @change="onSelectChange"
          @search="onSearch"
          @blur="onBlur"
        >
          <div v-if="loading" slot="notFoundContent" class="loading">
            <a-spin size="small" />
          </div>
          <template v-for="(item, index) in localList">
            <a-select-option
              :key="index"
              :disabled="item.disabled"
              :value="item[localAttrKeys.value]"
              :title="item | labelFormat(localAttrKeys.selectLabel)"
              :label="item | labelFormat(localAttrKeys.label)"
            >
              {{ item | labelFormat(localAttrKeys.selectLabel) }}
            </a-select-option>
          </template>
        </a-select>
      </a-tooltip>
      <span
        :class="['suffix-icon', { 'not-allowed': disabled }]"
        @click="onShow"
      >
        <sass-icon :type="gengduo" style="font-size: 18px" />
      </span>
    </div>
    <select-modal
      :title="title"
      :selected.sync="selectedList"
      :visible.sync="visible"
      :columns="columns"
      :multiple="multiple"
      :params="params"
      :attr-keys="attrKeys"
      :filter-config="filterConfig"
      :container-selector="containerSelector"
      :list="localList"
      :loading="loading"
      :total="total"
      :pageSizeOptions="pageSizeOptions"
      @reset="(e) => $emit('reset', e)"
      @queryList="queryList"
    >
      <slot ></slot>
    </select-modal>
  </span>
</template>

<script>
import { uniqBy } from 'lodash'
import config from './multiSelectConfig'
import selectModal from './components/selectModal'
import { axios } from '@/utils/request'
export default {
  name: 'MultiSelect',
  filters: {
    // label 格式化 过滤器
    labelFormat(option = {}, labels = []) {
      // 没有指定字段，直接返回 ”“
      if (!labels) {
        return ''
      } else if (labels.length === 1) {
        // 如果只有一个，则直接获取
        return option[labels[0]]
      } else {
        // 如果有多个，则汇总获取。
        return labels.reduce((prev, cur) => option[prev] + ` ${option[cur]}`)
      }
    }
  },
  props: {
    type: {
      type: String,
      required: true
    },
    value: [Array, Object, String, Number],
    // 是否多选
    multiple: {
      type: Boolean,
      default: false
    },
    // 是否需要有默认值
    required: {
      type: Boolean,
      default: false
    },
    // 是否允许清空
    allowClear: {
      type: Boolean,
      default: true
    },
    // 输入提示内容
    placeholder: {
      type: String,
      default: ''
    },
    // 默认列表数据，
    // 静态列表可用，
    list: {
      type: Array
    },
    // 组件宽度
    width: {
      type: [String, Number],
      default: '100%'
    },
    // 是否禁用
    disabled: {
      type: Boolean,
      default: false
    },
    // 弹框挂载的元素实例
    containerSelector: String,
    // 额外的动态请求参数
    params: {
      type: Object,
      default() {
        return {}
      }
    },
    // 自定义下拉菜单类名
    dropdownClassName: {
      type: String,
      default: ''
    },
    // 下拉菜单和选择器同宽
    dropdownMatchSelectWidth: {
      type: Boolean,
      default: false
    },
    // 自定义动态标题
    dynamicTitle: {
      type: String,
      default: ''
    },
    // 只有一个选项的时候选中其值
    selectOnlyOne: {
      type: Boolean,
      default: false
    },
    gengduo: {
      type: String,
      default: 'hi-gengduo1'
    },
    // 接口请求必须依赖传入的额外参数params
    layoutParams: {
      type: Boolean,
      default: false
    },
    // 指定每页可以显示多少条
    pageSizeOptions: {
      type: Array,
      default() {
        return ['10', '20', '50', '100', '200', '500']
      }
    }
  },
  data() {
    return {
      title: config[this.type] ? config[this.type].title : '',
      columns: config[this.type] ? config[this.type].columns : [],
      attrKeys: config[this.type] ? config[this.type].attrKeys : {},
      visible: false,
      timer: null,
      key: 0, // 重绘select，避免多选的时候错位
      // 请求后端来的数据
      remotList: [],
      loading: false,
      total: 0,
      isSearching: false // 是否正在搜索的状态
    }
  },
  watch: {
    selectedList: {
      handler(val) {
        if ((!val || (Array.isArray(val) && val.length === 0)) && this.remotList.length) {
          this.$emit('firstGetList', this.remotList)
        }
      },
      deep: true
    },
    remotList: {
      handler(val) {
        // selectOnlyOne下拉框只有一个选项的时候初始默认选择第一项  或者required不管下拉框有多少选项初始默认选择第一项
        const flag =
          this.multiple && this.value
            ? this.value.length
            : this.value && this.value.id
        if ((!this.selectedList || (Array.isArray(this.selectedList) && this.selectedList.length === 0)) && val.length) {
          this.$emit('firstGetList', val)
        }
        if (
          (val.length === 1 && !flag && this.selectOnlyOne) ||
          (!flag && this.required)
        ) {
          this.selectedList = this.multiple
            ? [this.localList[0]]
            : this.localList[0]
        }
      },
      deep: true
    }
  },
  computed: {
    filterConfig() {
      const arr = config[this.type] ? config[this.type].filterConfig : []
      return arr
    },
    // 属性key的组合
    localAttrKeys() {
      return {
        searchContent: 'searchContent',
        pageNo: 'pageNo',
        pageSize: 'pageSize',
        total: 'total',
        value: 'id',
        selectLabel: ['code', 'name'],
        label: ['code', 'name'],
        ...this.attrKeys
      }
    },
    // 已选中的完整数据
    selectedList: {
      get() {
        return this.value && JSON.stringify(this.value) !== '{}'
          ? this.value
          : []
      },
      set(val) {
        ++this.key
        this.$emit('input', val)
        this.$emit('change', val)
      }
    },
    // select框的value值
    selectValue() {
      const {
        multiple,
        selectedList,
        localAttrKeys: { value }
      } = this
      return multiple
        ? Array.isArray(selectedList)
          ? selectedList.map((item) => item[value])
          : []
        : selectedList
          ? selectedList[value]
          : ''
    },
    // 组合需要在select的option中展示的所有条目
    localList() {
      let {
        list,
        selectedList,
        remotList,
        localAttrKeys: { value }
      } = this
      list = Array.isArray(list) ? list : []
      // fix:直选中ID的清空。 因为有可能没有调用接口，初始化的时候需要将已选择的作为option
      const selected = Array.isArray(selectedList)
        ? selectedList
        : selectedList
          ? [selectedList]
          : []
      remotList = Array.isArray(remotList) ? remotList : []
      // 搜索状态或者是弹框打开情况下，待选择的列表中只含远程获取的数据，不包含当前已选择的数据，
      return this.isSearching || this.visible
        ? [...list, ...remotList]
        : uniqBy([...list, ...remotList, ...selected], value)
    },
    // select宽度
    localWidth() {
      return typeof this.width === 'number' ? `${this.width}px` : this.width
    },
    // tooltip 显示字段
    tooltip() {
      const {
        multiple,
        selectedList,
        localAttrKeys: { label }
      } = this
      if (!selectedList || selectedList.length === 0) return ''
      if (multiple) {
        const labels = Array.isArray(selectedList)
          ? selectedList.map((item) => {
            if (label.length === 1) {
              // 如果只有一个，则直接获取
              return item[label[0]]
            } else {
              // 如果有多个，则汇总获取。
              return label.reduce(
                (prev, cur) => item[prev] + ` ${item[cur]}`
              )
            }
          })
          : []
        return labels.join('，')
      } else {
        if (label.length === 1) {
          // 如果只有一个，则直接获取
          return selectedList[label[0]]
        } else {
          // 如果有多个，则汇总获取。
          return label.reduce(
            (prev, cur) => selectedList[prev] + ` ${selectedList[cur]}`
          )
        }
      }
    }
  },
  methods: {
    isNull(v) {
      return (
        v === undefined ||
        v === null ||
        v === '' ||
        v === -1 ||
        (typeof v === 'number' && isNaN(v))
      )
    },
    // 点击三个点事件返回函数
    onShow() {
      if (this.disabled) return
      this.visible = true
      this.loading = true
    },
    // 光标移除
    onBlur(e) {
      console.log(e)
      this.$emit('blur', e)
    },
    // 选择框选中条目发生改变
    onSelectChange(value) {
      const {
        localList,
        multiple,
        required,
        title,
        attrKeys: { value: attrValue }
      } = this
      if (multiple && required && !(value && value.length)) {
        this.$message.error(`请至少保留一条${title}数据！`)
        return
      }
      let selected
      if (multiple) {
        selected = value.map((item) => {
          return localList.find((i) => i[attrValue] === item)
        })
      } else {
        selected = localList.find((item) => item[attrValue] === value)
      }
      this.selectedList = selected
    },
    // 选择框输入文本改变时的返回函数
    onSearch(value = '') {
      const params = {
        [this.localAttrKeys.searchContent]: value,
        ...this.params
      }
      // 过滤掉空白的值（’‘，null,undefined,-1, NaN）,不传递给后端
      for (const key in params) {
        if (this.isNull(params[key])) {
          delete params[key]
        }
      }
      if (value !== '') {
        this.loading = true
      } else {
        this.loading = false
      }
      // 防抖操作
      clearTimeout(this.timer)
      this.timer = setTimeout(() => {
        this.queryList(params, true)
      }, 300)
    },
    // 接口调用
    queryList(params = {}, isSearch = false) {
      this.remotList = []
      this.isSearching = isSearch
      // 防抖操作
      clearTimeout(this.timer)
      if (this.layoutParams && !Object.keys(this.params).length) {
        this.total = 0
        return
      }
      this.timer = setTimeout(() => {
        this.loading = true
        const query = {
          ...params,
          ...this.params
        }
        // 过滤掉空白的值（’‘，null,undefined,-1, NaN）,不传递给后端
        for (const key in query) {
          if (this.isNull(query[key])) {
            delete query[key]
          }
        }
        // 特殊下拉选择参数需要以数组形式传参
        if (this.filterConfig) {
          this.filterConfig.forEach(filter => {
            if (filter.isArray && query[filter.key]) {
              query[filter.key] = [query[filter.key]]
            }
          })
        }
        axios
          .post(config[this.type].url, query)
          .then((res) => {
            if (+res.code === 0) {
              // this.remotList = res.data?.records || []
              const dataName = config[this.type].dataName || 'records'
              this.remotList = res.data && res.data[dataName] ? res.data[dataName] : []
              this.total = res.total
            }
          })
          .catch(console.log)
          .finally(() => {
            this.loading = false
          })
      }, 200)
    }
  },
  created() {
    const { list, localAttrKeys } = this
    if (list && list.length > 0) return
    const params = {
      [localAttrKeys.pageNo]: 1,
      [localAttrKeys.pageSize]: 20,
      ...this.params
    }
    // 过滤掉空白的值（’‘，null,undefined,-1, NaN）,不传递给后端
    for (const key in params) {
      if (this.isNull(params[key])) {
        delete params[key]
      }
    }
    this.queryList(params)
  },
  components: {
    selectModal
  }
}
</script>
<style lang="less" scoped>
.slect-plus {
  display: inline-block;
  position: relative;
  min-width: 100px;
}
.select-area {
  position: relative;
  line-height: 28px;
}
.suffix-icon {
  cursor: pointer;
  padding: 0 5px;
  position: absolute;
  right: 0;
  top: 3px;
}
.not-allowed {
  cursor: not-allowed;
}
.slect-plus::v-deep {
  .ant-select-selection__clear {
    right: 20px;
  }
  .ant-select-no-arrow .ant-select-selection__rendered {
    margin-right: 20px;
  }
  // 解决多选换行问题
  .ant-select-selection--multiple {
    max-height: 28px;
    // overflow: hidden;
    li {
      &:first-child {
        max-width: 55%;
      }
    }
  }
  .loading {
    height: 90px;
    line-height: 90px;
    text-align: center;
  }
}
</style>
